/**
 * Freshness — Level 0 primitive. Pure, no I/O, no React.
 *
 * How old is the data a surface is showing, and should it say so?
 *
 * SINGLE-SOURCE -> MULTI-SURFACE PROJECTION. Freshness is not a frontend
 * opinion: the backend already computes it and ships it on every whereabouts
 * payload as `_snapshot_computed_at` / `_snapshot_age_hours`. The Slack board
 * renders it from the same fields with the same 26h threshold
 * (`utils/primitives/whereabouts_render.py :: _STALE_AFTER_HOURS`). This module
 * PROJECTS that contract onto the web surface — it does not re-declare it.
 * If the threshold ever changes, it changes once, at the source, and both
 * surfaces follow. A second definition here would be a second source of truth.
 *
 * Why 26h and not 24h: the snapshot refreshes every ~24h, so a payload up to a
 * day old is NORMAL and needs no caveat. Past that, something is actually late.
 */

/** Matches the backend's `_STALE_AFTER_HOURS`. Change it there, not here. */
export const STALE_AFTER_HOURS = 26;

/**
 * Describe how fresh a payload is.
 *
 * @param {object} payload  a response carrying `_snapshot_computed_at` and/or
 *                          `_snapshot_age_hours` (both optional)
 * @param {Date}   [now]    injectable for tests — never read the clock twice
 * @param {Date}   [fetchedAt]  when the CLIENT received this payload (the
 *                          hook's `fetchedAt`). `_snapshot_age_hours` is the
 *                          age AT FETCH TIME — a long-lived tab must add the
 *                          time elapsed since, or it reports "11 hours ago"
 *                          (and isStale:false) forever. Omitted => the server
 *                          age is used as-is (fresh-fetch behaviour).
 * @returns {{ageHours: number|null, isStale: boolean, label: string|null,
 *            computedAt: Date|null}}
 *
 * `label` is null when we genuinely don't know the age — callers must render
 * nothing rather than guess. Claiming "just now" for an unknown age is the
 * confidently-wrong failure this whole feature has been fighting.
 */
export function describeFreshness(payload, now = new Date(), fetchedAt = null) {
  const empty = { ageHours: null, isStale: false, label: null, computedAt: null };
  if (!payload) return empty;

  const computedAt = payload._snapshot_computed_at
    ? new Date(payload._snapshot_computed_at)
    : null;
  const validDate = computedAt && !Number.isNaN(computedAt.getTime());

  // Prefer the server's own age; fall back to computing it from the timestamp.
  // The server age is frozen at fetch time — add the client-side time elapsed
  // since the payload arrived, or a long-lived tab never crosses the stale
  // threshold. (The timestamp fallback already measures against `now`, so it
  // needs no correction.)
  let ageHours = null;
  if (typeof payload._snapshot_age_hours === 'number') {
    ageHours = payload._snapshot_age_hours;
    const fetchedMs =
      fetchedAt instanceof Date && !Number.isNaN(fetchedAt.getTime())
        ? fetchedAt.getTime()
        : null;
    if (fetchedMs !== null) {
      const elapsedHours = (now.getTime() - fetchedMs) / 3_600_000;
      if (elapsedHours > 0) ageHours += elapsedHours; // skew: never subtract
    }
  } else if (validDate) {
    ageHours = (now.getTime() - computedAt.getTime()) / 3_600_000;
  }

  if (ageHours === null) return { ...empty, computedAt: validDate ? computedAt : null };
  if (ageHours < 0) ageHours = 0; // clock skew — never render "in the future"

  return {
    ageHours,
    isStale: ageHours >= STALE_AFTER_HOURS,
    label: humanizeAge(ageHours),
    computedAt: validDate ? computedAt : null,
  };
}

/** "just now" | "12 min ago" | "3 hours ago" | "2 days ago" — pure. */
export function humanizeAge(ageHours) {
  if (typeof ageHours !== 'number' || Number.isNaN(ageHours)) return null;
  const mins = Math.round(ageHours * 60);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(ageHours);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(ageHours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
